import React, { useEffect, useState } from 'react';
import * as AppPics from 'assets/images';
import { AppTextField, AppUploadBlock, AppRadioBox, AppButton } from 'components';
import Joi from 'joi';
import { checkForErrors } from 'utilities/fns';
import { useHistory, useParams, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { notifier } from 'components/Toaster';
import { getRandomUid } from 'utilities/uuid.service';
import { useQueryClient } from 'react-query';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import useSupportCategory from 'hooks/API/POST/useSupportCategory';
import useGetOneSupportCategory from 'hooks/API/GET/useGetOneSupportCategory';

function ManageSupportCategory() {
  const { categoryId } = useParams();

  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();

  const params = new URLSearchParams(location.search);
  const redirectBack = params.get('redirect_back');

  const [category, setCategory] = useState('');
  const [url, setUrl] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [categoryImage, setCategoryImage] = useState(null);
  const [categoryImageFullPath, setCategoryImageFullPath] = useState(null);
  const [categoryImageFullPathUploadData, setCategoryImageUploadData] = useState('');
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [referenceKey, setReferenceKey] = useState('');
  const [formErrors, setFormErrors] = useState([]);
  const [validationErrorServer, setValidationErrorServer] = useState({});
  const [deletedImage, setDeletedImage] = useState(false);

  const { mutate, isLoading, isError, isSuccess, data, error } = useSupportCategory();

  const clearForm = () => {
    setCategory('');
    setUrl('');
    setCategoryImage(null);
    setSelectedType('');
    setCategoryImageFullPath(null);
    setCategoryImageUploadData(null);
    setFormErrors([]);
    setValidationErrorServer({});
    setDisableSubmit(false);
    setDeletedImage(false);
    setReferenceKey(getRandomUid());
  };

  const handleGoBack = () => {
    if (redirectBack) {
      history.replace(decodeURIComponent(redirectBack));
    } else {
      history.goBack();
    }
  };

  useEffect(() => {
    if (categoryId === 'add') {
      clearForm();
      queryClient.removeQueries(['getSingleCategory', categoryId]);
      return;
    }
  }, [categoryId, queryClient]);

  const getSingleCategory = useGetOneSupportCategory({
    enabled: categoryId !== 'add',
    categoryId,
    config: { params: { categoryId } },
  });

  useEffect(() => {
    if (categoryId !== 'add' && getSingleCategory?.data && getSingleCategory?.isSuccess) {
      const currentData = getSingleCategory.data?.data?.data || getSingleCategory.data?.data;

      setCategory(currentData.title || '');
      setUrl(currentData.url || '');
      setCategoryImage(currentData.image || null);
      setCategoryImageFullPath(currentData.categoryImg || null);
      setSelectedType(currentData.categorySection || '');
      setReferenceKey(currentData.referenceKey || getRandomUid());
      setDeletedImage(false);
    }
  }, [categoryId, getSingleCategory?.data, getSingleCategory?.isSuccess]);

  const handleCategoryChange = (event) => {
    const newValue = event.target.value;
    setCategory(newValue);
  };

  const handleImageChange = ({ file, uploadedData }) => {
    setCategoryImageUploadData(uploadedData);
    setCategoryImageFullPath(`${uploadedData.basePath}${uploadedData.file}`);
    setCategoryImage(uploadedData.file);
    setDeletedImage(false);
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
  };

  function handleSave() {
    setFormErrors([]);
    if (disableSubmit) return;

    const data = {
      name: category.trim(),
      url: url.trim(),
      image: deletedImage ? null : categoryImage ?? null,
      section: selectedType,
      ...(categoryId && categoryId !== 'add' ? { categoryId } : {}),
    };
    if (deletedImage) {
      data.imageDeleted = true;
    }

    const validation = categorySchema.validate(data, { abortEarly: false, allowUnknown: true });
    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    mutate(data, {
      onError(failData) {
        const errorData = failData?.response?.data;
        const errorMessage = errorData?.message;
        const validationErrors = errorData?.errors;

        setValidationErrorServer(validationErrors || {});
        notifier.error(errorMessage);
      },

      onSuccess(response) {
        const successMessage = response?.data?.data?.message || response?.data?.message;
        notifier.success(successMessage);

        handleGoBack();

        queryClient.invalidateQueries(['support-categories']);
        // queryClient.invalidateQueries(['getSingleCategory']);
      },
    });
  }

  const errors = checkForErrors(formErrors);

  const handleRadioKeyDown = (e, value) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTypeChange(value);
    }
  };

  const handleDeleteCategoryImage = () => {
    setCategoryImage(null);
    setCategoryImageFullPath(null);
    setCategoryImageUploadData(null);
    setDeletedImage(true);
    notifier.success('Image deleted successfully');
  };

  return (
    <>
      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <button className="btn-back" onClick={handleGoBack}>
                <img src={AppPics.BackSvg} alt="Back" />
              </button>
            </li>
          </ul>

          <div
            className="tab__content
          ___adjust___tab___padding"
          >
            <div className="tab__content-actHead">
              <AppBreadcrumbs
                links={[
                  { label: 'Dashboard', to: '/dashboard' },
                  { label: 'Support Category', to: '/support/category' },
                  { label: 'Manage', to: '/support/category/add' },
                ]}
                current={categoryId === 'add' ? 'Add Genre' : 'Update Genre'}
              />
            </div>
            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                <div className="category-form">
                  <div className="row mt-4">
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      {categoryId !== 'add' ? (
                        <h2 className="prime-head p-0 boldtitle">Update Genre</h2>
                      ) : (
                        <h2 className="prime-head p-0 boldtitle">Add Genre</h2>
                      )}
                      {validationErrorServer && (
                        <ul className="error-list">
                          {Object.values(validationErrorServer).map((err, idx) => (
                            <li key={idx}>{err.msg}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <AppTextField
                        labelProps={{ label: 'Support category *', htmlFor: 'support-category' }}
                        inputProps={{
                          placeholder: 'Support Category',
                          id: 'categoryName',
                          name: 'categoryName',
                          value: category,
                          onChange: handleCategoryChange,
                          tabIndex: 1,
                        }}
                        fieldErrors={errors.name}
                      />
                      {errors.name && <div className="text-danger">{errors.name.errorMessage}</div>}
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <AppTextField
                        labelProps={{
                          label: 'URL',
                          htmlFor: 'url',
                        }}
                        inputProps={{
                          placeholder: 'url',
                          id: 'url',
                          name: 'url',
                          value: url,
                          onChange: (event) => setUrl(event.target.value),
                          tabIndex: 2,
                        }}
                        fieldErrors={errors['url']}
                      />
                      {errors.url && <div className="text-danger">{errors.url.errorMessage}</div>}
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <AppUploadBlock
                        labelProps={{ label: 'Support Image' }}
                        props={{
                          isImage: true,
                          isPublic: true,
                          referenceKey: 'SUPPORT_CATEGORY',
                          uploadCallback: (value) => {
                            setDisableSubmit(value);
                          },
                          handleChange: (data = {}) => {
                            const { file, uploadedData } = data;
                            handleImageChange({ file, uploadedData });
                            setDeletedImage(false);
                          },
                          fileData: {
                            fullPath: deletedImage ? null : categoryImageFullPath,
                          },
                          clearFile: deletedImage,
                          tabIndex: 3,
                        }}
                      />
                      {(categoryImage || categoryImageFullPath) && !deletedImage && (
                        <button
                          className="actions-icons mx-2 text-danger mt-2"
                          title="Delete Genre Image"
                          onClick={handleDeleteCategoryImage}
                          type="button"
                        >
                          <img src={AppPics.DeleteSvg} alt="Delete" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="d-flex justify-content-end">
                    <AppButton
                      type="submit"
                      variantClass="btn-primary mx-3"
                      style={{ minWidth: '1.5rem' }}
                      onClick={handleSave}
                      disabled={disableSubmit || isLoading}
                      tabIndex={6}
                    >
                      {isLoading ? 'Saving...' : 'Save Category'}
                    </AppButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const categorySchema = Joi.object({
  name: Joi.string().min(1).max(100).required().messages({
    'string.base': 'Category name must be a string.',
    'string.empty': 'Category name cannot be empty.',
    'string.max': 'Category name must be at most 100 characters long.',
    'any.required': 'Category name is required.',
  }),
});

export default ManageSupportCategory;
